import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Layers, Clock, Package, Ruler } from "lucide-react";
import { BRANDS, GRADE_FILTERS } from "@/data/brands";
import { waLink, SKYLINE_BG } from "@/lib/config";

export default function BrandsShowcase() {
  const [filter, setFilter] = useState("All Grades");
  const visible = filter === "All Grades" ? BRANDS : BRANDS.filter((b) => b.grades.includes(filter));

  return (
    <section id="brands" className="relative bg-ink py-20 lg:py-28 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img src={SKYLINE_BG} alt="" className="w-full h-full object-cover opacity-[0.08]" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/85 to-ink" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-brand inline-block" /> The Brand Matrix
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">
              Every Reputed Brand.
              <br />
              One Supply Line.
            </h2>
          </div>
          <p className="text-sm sm:text-base text-gray-400 max-w-md leading-relaxed">
            Filter by grade spec. Every dispatch carries batch test certificates and tamper-proof,
            moisture-barrier bagging — verified at the railhead before transit.
          </p>
        </div>

        <div data-testid="brands-filter-tabs" className="flex flex-wrap gap-2 mb-10">
          {GRADE_FILTERS.map((g) => (
            <button
              key={g}
              onClick={() => setFilter(g)}
              data-testid={`brand-filter-${g.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative font-mono text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
                filter === g
                  ? "border-brand text-ink bg-brand"
                  : "border-white/15 text-gray-400 hover:text-white hover:border-brand/50"
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {visible.map((b, i) => (
              <motion.article
                layout
                key={b.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`brand-card-${b.id}`}
                className="group bg-surface border border-white/10 hover:border-brand/40 hover:-translate-y-1 transition-all duration-200 shadow-xl p-6 flex flex-col"
              >
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <h3 className="font-heading text-xl sm:text-2xl font-bold uppercase tracking-normal text-white">
                      {b.name}
                    </h3>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">{b.tag}</p>
                  </div>
                  <span className="font-mono text-xs text-brand border border-brand/40 px-2 py-1">
                    {b.grades[0]}
                  </span>
                </div>

                <div className="mt-5 space-y-2.5 text-sm flex-1">
                  <p className="flex items-center gap-2.5 text-gray-400">
                    <Ruler className="h-4 w-4 text-brand/70 shrink-0" />
                    <span className="font-mono text-xs">{b.strength}</span>
                  </p>
                  <p className="flex items-center gap-2.5 text-gray-400">
                    <Clock className="h-4 w-4 text-brand/70 shrink-0" />
                    <span className="font-mono text-xs">{b.setting}</span>
                  </p>
                  <p className="flex items-center gap-2.5 text-gray-400">
                    <Package className="h-4 w-4 text-brand/70 shrink-0" />
                    <span className="font-mono text-xs">{b.packaging}</span>
                  </p>
                  <p className="flex items-center gap-2.5 text-gray-400">
                    <Layers className="h-4 w-4 text-brand/70 shrink-0" />
                    <span className="font-mono text-xs">{b.applications}</span>
                  </p>
                </div>

                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                  <div className="flex gap-1.5">
                    {b.grades.map((g) => (
                      <span key={g} className="font-mono text-[10px] uppercase bg-ink border border-white/10 text-gray-400 px-2 py-0.5">
                        {g}
                      </span>
                    ))}
                  </div>
                  <a
                    href={waLink(`Quote request: ${b.name} cement (${b.grades.join("/")}). Quantity: ____ bags. Site: ____`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={b.id === "ultratech" ? "brand-btn-inquire-quote" : `brand-quote-${b.id}`}
                    className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-wide text-brand hover:text-brand-dark transition-colors shrink-0"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    Quote
                  </a>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
